"use client"

import { isAnalyticsSuppressed } from "@src/utils/analyticsSuppression"
import {
  APP_ENV,
  IS_PRODUCTION_ENV,
  MIXPANEL_TOKEN,
} from "@src/utils/environment"
import mixpanel, { type Mixpanel } from "mixpanel-browser"
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react"

export const MixpanelContext = createContext<Mixpanel | null>(null)

export function MixpanelProvider({ children }: { children: ReactNode }) {
  const [mixpanelInstance, setMixpanelInstance] = useState<Mixpanel | null>(
    null
  )

  useEffect(() => {
    if (!IS_PRODUCTION_ENV) return

    if (!MIXPANEL_TOKEN) {
      // biome-ignore lint/suspicious/noConsole: <explanation>
      console.warn("Mixpanel token is not configured")
      return
    }

    // Snapshot suppression state at init. If the user starts in
    // confidential mode or on a sensitive route, initialize without
    // auto-pageview and in an opted-out state so nothing fires. The
    // useAnalyticsOptOut hook in TrackingProvider handles dynamic
    // state changes afterward.
    const suppressedAtInit = isAnalyticsSuppressed()

    try {
      // Strip hash fragments from all URLs so secrets
      // in the URL hash are never sent to Mixpanel.
      // Patches the internal URL source used by track(), session
      // recording, and autocapture. Must run BEFORE init(): autocapture
      // fires the first $mp_web_page_view synchronously inside init(),
      // and `mixpanel._` is the SDK's shared utils singleton, already
      // attached at module load.
      // biome-ignore lint/suspicious/noExplicitAny: accessing internal SDK util
      ;(mixpanel as any)._.info.currentUrl = () =>
        window.location.href.split("#")[0]

      mixpanel.init(MIXPANEL_TOKEN, {
        debug: APP_ENV === "development",
        track_pageview: suppressedAtInit ? false : "url-with-path",
        persistence: "localStorage",
      })

      if (suppressedAtInit) {
        mixpanel.opt_out_tracking()
      }

      // Override the auto-captured $referrer super property so hash
      // fragments are stripped. Mixpanel reads `document.referrer`
      // directly (bypassing `_.info.currentUrl`) when registering
      // $referrer at init, which would leak the hash on any hard
      // navigation from /gift#SECRET or /deal#SECRET.
      mixpanel.register({
        $referrer: document.referrer.split("#")[0],
      })

      setMixpanelInstance(mixpanel)
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: <explanation>
      console.error("Failed to initialize Mixpanel:", error)
    }
  }, [])

  return (
    <MixpanelContext.Provider value={mixpanelInstance}>
      {children}
    </MixpanelContext.Provider>
  )
}

export function useMixpanel() {
  const mixpanel = useContext(MixpanelContext)

  // Return null if mixpanel is not initialized (no token, init failed, or still loading)
  if (!mixpanel) return null

  return mixpanel
}
